import React, { useState } from 'react';
import { motion } from 'framer-motion';
import {
  Building2, Plus, Search, MapPin, DollarSign, Calendar, Phone,
  Edit, Eye, Trash2, Filter, Download, Building, Warehouse, Store,
  AlertCircle, CheckCircle, XCircle, ArrowRight, FileText, Users
} from 'lucide-react';
import PropertyForm from '../forms/PropertyForm';

const Properties = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedFilter, setSelectedFilter] = useState('all');
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingProperty, setEditingProperty] = useState(null);

  const propertyTypes = [
    { id: 'store', label: 'Store', icon: Store, color: 'blue' },
    { id: 'warehouse', label: 'Warehouse', icon: Warehouse, color: 'orange' },
    { id: 'building', label: 'Building', icon: Building, color: 'green' },
    { id: 'office', label: 'Office Space', icon: Building2, color: 'purple' },
  ];

  const statusOptions = ['all', 'occupied', 'vacant', 'maintenance', 'pending'];

  // Mock properties data
  const [propertiesList, setPropertiesList] = useState([
    {
      id: 'PROP-001',
      internalId: 'LOC-RYD-001',
      name: 'Riyadh Main Store',
      type: 'store',
      address: 'King Fahd Road, Riyadh',
      city: 'Riyadh',
      lat: 24.729013,
      lng: 46.642046,
      area: 500, // sqm
      status: 'occupied',
      monthlyRent: 15000,
      currentTenant: 'ABC Retail Company',
      tenantContact: '+966 50 123 4567',
      contractStart: '2023-01-15',
      contractEnd: '2026-01-14',
      contractDuration: '3 years',
      nextPaymentDue: '2024-02-15',
      daysUntilDue: 2,
      totalRentals: 1,
      issuesCount: 0,
      notes: 'Prime location, high foot traffic'
    },
    {
      id: 'PROP-002',
      internalId: 'LOC-JED-001',
      name: 'Jeddah Warehouse Complex',
      type: 'warehouse',
      address: 'Industrial Area, Jeddah',
      city: 'Jeddah',
      lat: 21.4858,
      lng: 39.1925,
      area: 2000,
      status: 'occupied',
      monthlyRent: 25000,
      currentTenant: 'XYZ Logistics',
      tenantContact: '+966 50 987 6543',
      contractStart: '2022-06-01',
      contractEnd: '2025-05-31',
      contractDuration: '3 years',
      nextPaymentDue: '2024-02-20',
      daysUntilDue: 7,
      totalRentals: 1,
      issuesCount: 2,
      notes: 'Large storage capacity, loading docks available'
    },
    {
      id: 'PROP-003',
      internalId: 'LOC-DMM-001',
      name: 'Dammam Office Building',
      type: 'building',
      address: 'Corniche Road, Dammam',
      city: 'Dammam',
      lat: 26.4207,
      lng: 50.0888,
      area: 1200,
      status: 'occupied',
      monthlyRent: 18000,
      currentTenant: 'Tech Solutions Inc.',
      tenantContact: '+966 50 555 1234',
      contractStart: '2023-03-01',
      contractEnd: '2026-02-28',
      contractDuration: '3 years',
      nextPaymentDue: '2024-02-10',
      daysUntilDue: -3,
      totalRentals: 1,
      issuesCount: 1,
      notes: 'Modern office building with parking'
    },
    {
      id: 'PROP-004',
      internalId: 'LOC-RYD-002',
      name: 'Riyadh Retail Space',
      type: 'store',
      address: 'Olaya Street, Riyadh',
      city: 'Riyadh',
      lat: 24.719000,
      lng: 46.612046,
      area: 300,
      status: 'vacant',
      monthlyRent: 12000,
      currentTenant: null,
      tenantContact: null,
      contractStart: null,
      contractEnd: null,
      contractDuration: null,
      nextPaymentDue: null,
      daysUntilDue: null,
      totalRentals: 0,
      issuesCount: 0,
      notes: 'Available for rent, good visibility'
    },
    {
      id: 'PROP-005',
      internalId: 'LOC-KBU-001',
      name: 'Khobar Storage Facility',
      type: 'warehouse',
      address: 'Industrial Zone, Khobar',
      city: 'Khobar',
      lat: 26.2172,
      lng: 50.1971,
      area: 1500,
      status: 'maintenance',
      monthlyRent: 20000,
      currentTenant: 'Storage Co.',
      tenantContact: '+966 50 444 5678',
      contractStart: '2021-01-01',
      contractEnd: '2024-12-31',
      contractDuration: '4 years',
      nextPaymentDue: '2024-02-15',
      daysUntilDue: 2,
      totalRentals: 1,
      issuesCount: 3,
      notes: 'Under maintenance - roof repairs'
    },
  ]);

  const handleSaveProperty = (propertyData) => {
    if (editingProperty) {
      // Update existing property
      setPropertiesList(prev => prev.map(p => 
        p.id === editingProperty.id ? { ...p, ...propertyData, id: editingProperty.id } : p
      ));
      setEditingProperty(null);
    } else {
      // Add new property
      const newProperty = {
        ...propertyData,
        id: `PROP-${String(propertiesList.length + 1).padStart(3, '0')}`,
        totalRentals: 0,
        issuesCount: 0,
        currentTenant: null,
        tenantContact: null,
        contractStart: null,
        contractEnd: null,
        contractDuration: null,
        nextPaymentDue: null,
        daysUntilDue: null,
      };
      setPropertiesList(prev => [...prev, newProperty]);
      setShowCreateModal(false);
    }
  };

  const handleDeleteProperty = (propertyId) => {
    if (window.confirm('Are you sure you want to delete this property?')) {
      setPropertiesList(prev => prev.filter(p => p.id !== propertyId));
      if (selectedProperty?.id === propertyId) {
        setSelectedProperty(null);
      }
    }
  };

  const filteredProperties = propertiesList.filter(prop => {
    const matchesSearch = prop.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      prop.internalId.toLowerCase().includes(searchQuery.toLowerCase()) ||
      prop.address.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (prop.currentTenant && prop.currentTenant.toLowerCase().includes(searchQuery.toLowerCase()));
    
    const matchesFilter = selectedFilter === 'all' || prop.status === selectedFilter;
    
    return matchesSearch && matchesFilter;
  });

  const getStatusColor = (status) => {
    switch (status) {
      case 'occupied': return 'bg-green-100 text-green-700';
      case 'vacant': return 'bg-gray-100 text-gray-700';
      case 'maintenance': return 'bg-orange-100 text-orange-700';
      case 'pending': return 'bg-blue-100 text-blue-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  const getDueDateColor = (daysUntilDue) => {
    if (daysUntilDue === null) return 'text-gray-500';
    if (daysUntilDue < 0) return 'text-red-600 font-semibold';
    if (daysUntilDue <= 7) return 'text-orange-600 font-semibold';
    return 'text-green-600';
  };

  const PropertyCard = ({ property }) => {
    const typeInfo = propertyTypes.find(t => t.id === property.type);
    const TypeIcon = typeInfo?.icon || Building2;

    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        whileHover={{ scale: 1.02, y: -2 }}
        className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 hover:shadow-md transition-all cursor-pointer"
        onClick={() => setSelectedProperty(property)}
      >
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-start gap-4 flex-1">
            <div className={`p-3 rounded-lg bg-${typeInfo?.color || 'gray'}-100`}>
              <TypeIcon className={`w-6 h-6 text-${typeInfo?.color || 'gray'}-600`} />
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-1">
                <h3 className="text-lg font-semibold text-gray-900">{property.name}</h3>
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(property.status)}`}>
                  {property.status}
                </span>
              </div>
              <p className="text-sm text-gray-600 mb-2">{property.internalId}</p>
              <div className="flex items-center gap-4 text-sm text-gray-600">
                <div className="flex items-center gap-1">
                  <MapPin className="w-4 h-4" />
                  <span>{property.city}</span>
                </div>
                <div className="flex items-center gap-1">
                  <Building2 className="w-4 h-4" />
                  <span>{property.area} sqm</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {property.status === 'occupied' && (
          <div className="border-t border-gray-200 pt-4 mt-4 space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">Tenant</span>
              <span className="text-sm font-medium text-gray-900">{property.currentTenant}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">Monthly Rent</span>
              <span className="text-sm font-semibold text-gray-900">SAR {property.monthlyRent.toLocaleString()}</span>
            </div>
            {property.nextPaymentDue && (
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Next Payment Due</span>
                <span className={`text-sm font-medium ${getDueDateColor(property.daysUntilDue)}`}>
                  {property.nextPaymentDue}
                  {property.daysUntilDue !== null && (
                    <span className="ml-2">
                      ({property.daysUntilDue < 0 ? `${Math.abs(property.daysUntilDue)} days overdue` :
                        property.daysUntilDue === 0 ? 'Due today' :
                        `${property.daysUntilDue} days`})
                    </span>
                  )}
                </span>
              </div>
            )}
            {property.contractEnd && (
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Contract End</span>
                <span className="text-sm text-gray-900">{property.contractEnd}</span>
              </div>
            )}
          </div>
        )}

        {property.status === 'vacant' && (
          <div className="border-t border-gray-200 pt-4 mt-4">
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">Monthly Rent</span>
              <span className="text-sm font-semibold text-gray-900">SAR {property.monthlyRent.toLocaleString()}</span>
            </div>
          </div>
        )}

        {property.issuesCount > 0 && (
          <div className="mt-4 pt-4 border-t border-gray-200">
            <div className="flex items-center gap-2 text-orange-600">
              <AlertCircle className="w-4 h-4" />
              <span className="text-sm font-medium">{property.issuesCount} active issue{property.issuesCount > 1 ? 's' : ''}</span>
            </div>
          </div>
        )}
      </motion.div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Header Actions */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex flex-col lg:flex-row gap-4 items-start lg:items-center justify-between">
          <div>
            <h2 className="text-xl font-bold text-gray-900">Properties</h2>
            <p className="text-sm text-gray-600 mt-1">
              Manage all properties, locations, and rental units
            </p>
          </div>
          <div className="flex gap-3">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                placeholder="Search properties..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm"
              />
            </div>
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => setShowCreateModal(true)}
              className="px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 flex items-center gap-2 text-sm font-medium transition-colors"
            >
              <Plus className="w-4 h-4" />
              New Property
            </motion.button>
          </div>
        </div>

        {/* Filters */}
        <div className="mt-4 flex flex-wrap gap-2">
          {statusOptions.map((status) => (
            <motion.button
              key={status}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => setSelectedFilter(status)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                selectedFilter === status
                  ? 'bg-amber-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              {status.charAt(0).toUpperCase() + status.slice(1)}
            </motion.button>
          ))}
        </div>
      </div>

      {/* Stats Summary */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        {[
          {
            label: 'Total Properties',
            value: propertiesList.length,
            icon: Building2,
            color: 'text-amber-600',
            bgColor: 'bg-amber-50'
          },
          {
            label: 'Occupied',
            value: propertiesList.filter(p => p.status === 'occupied').length,
            icon: CheckCircle,
            color: 'text-green-600',
            bgColor: 'bg-green-50'
          },
          {
            label: 'Vacant',
            value: propertiesList.filter(p => p.status === 'vacant').length,
            icon: XCircle,
            color: 'text-gray-600',
            bgColor: 'bg-gray-50'
          },
          {
            label: 'Monthly Revenue',
            value: `SAR ${propertiesList.filter(p => p.status === 'occupied').reduce((sum, p) => sum + p.monthlyRent, 0).toLocaleString()}`,
            icon: DollarSign,
            color: 'text-blue-600',
            bgColor: 'bg-blue-50'
          }
        ].map((stat, index) => {
          const Icon = stat.icon;
          return (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              className="bg-white rounded-xl shadow-sm border border-gray-200 p-5 hover:shadow-md transition-shadow"
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <p className="text-sm font-medium text-gray-600 mb-1">{stat.label}</p>
                  <p className={`text-2xl font-bold ${typeof stat.value === 'number' ? 'text-gray-900' : stat.color}`}>
                    {stat.value}
                  </p>
                </div>
                <div className={`p-3 rounded-lg ${stat.bgColor}`}>
                  <Icon className={`h-6 w-6 ${stat.color}`} />
                </div>
              </div>
            </motion.div>
          );
        })}
      </div>

      {/* Properties Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {filteredProperties.map((property) => (
          <PropertyCard key={property.id} property={property} />
        ))}
      </div>

      {/* Property Detail Modal */}
      {selectedProperty && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-xl shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto"
          >
            <div className="p-6 border-b border-gray-200 flex items-center justify-between">
              <h3 className="text-xl font-bold text-gray-900">Property Details</h3>
              <button
                onClick={() => setSelectedProperty(null)}
                className="p-2 hover:bg-gray-100 rounded-lg"
              >
                <XCircle className="w-5 h-5 text-gray-500" />
              </button>
            </div>
            <div className="p-6 space-y-6">
              {/* Property Info */}
              <div className="grid grid-cols-2 gap-6">
                <div>
                  <label className="text-sm font-medium text-gray-700">Property Name</label>
                  <p className="text-lg text-gray-900">{selectedProperty.name}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700">Internal ID</label>
                  <p className="text-lg text-gray-900">{selectedProperty.internalId}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700">Type</label>
                  <p className="text-lg text-gray-900 capitalize">{selectedProperty.type}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700">Status</label>
                  <span className={`inline-block px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(selectedProperty.status)}`}>
                    {selectedProperty.status}
                  </span>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700">Address</label>
                  <p className="text-lg text-gray-900">{selectedProperty.address}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700">City</label>
                  <p className="text-lg text-gray-900">{selectedProperty.city}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700">Area</label>
                  <p className="text-lg text-gray-900">{selectedProperty.area} sqm</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700">Monthly Rent</label>
                  <p className="text-lg font-semibold text-gray-900">SAR {selectedProperty.monthlyRent.toLocaleString()}</p>
                </div>
              </div>

              {selectedProperty.status === 'occupied' && (
                <>
                  <div className="border-t border-gray-200 pt-6">
                    <h4 className="text-lg font-semibold text-gray-900 mb-4">Tenant Information</h4>
                    <div className="grid grid-cols-2 gap-6">
                      <div>
                        <label className="text-sm font-medium text-gray-700">Tenant Name</label>
                        <p className="text-lg text-gray-900">{selectedProperty.currentTenant}</p>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-700">Contact</label>
                        <p className="text-lg text-gray-900">{selectedProperty.tenantContact}</p>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-700">Contract Start</label>
                        <p className="text-lg text-gray-900">{selectedProperty.contractStart}</p>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-700">Contract End</label>
                        <p className="text-lg text-gray-900">{selectedProperty.contractEnd}</p>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-700">Duration</label>
                        <p className="text-lg text-gray-900">{selectedProperty.contractDuration}</p>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-700">Next Payment Due</label>
                        <p className={`text-lg font-semibold ${getDueDateColor(selectedProperty.daysUntilDue)}`}>
                          {selectedProperty.nextPaymentDue}
                          {selectedProperty.daysUntilDue !== null && (
                            <span className="ml-2 text-sm">
                              ({selectedProperty.daysUntilDue < 0 ? `${Math.abs(selectedProperty.daysUntilDue)} days overdue` :
                                selectedProperty.daysUntilDue === 0 ? 'Due today' :
                                `${selectedProperty.daysUntilDue} days`})
                            </span>
                          )}
                        </p>
                      </div>
                    </div>
                  </div>
                </>
              )}

              {selectedProperty.notes && (
                <div className="border-t border-gray-200 pt-6">
                  <label className="text-sm font-medium text-gray-700">Notes</label>
                  <p className="text-lg text-gray-900 mt-2">{selectedProperty.notes}</p>
                </div>
              )}

              <div className="border-t border-gray-200 pt-6 flex gap-3 flex-wrap">
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => {
                    setEditingProperty(selectedProperty);
                    setSelectedProperty(null);
                  }}
                  className="px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 flex items-center gap-2 transition-colors"
                >
                  <Edit className="w-4 h-4" />
                  Edit Property
                </motion.button>
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => {
                    setSelectedProperty(null);
                  }}
                  className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 flex items-center gap-2 transition-colors"
                >
                  <FileText className="w-4 h-4" />
                  View Contracts
                </motion.button>
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => {
                    setSelectedProperty(null);
                  }}
                  className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 flex items-center gap-2 transition-colors"
                >
                  <DollarSign className="w-4 h-4" />
                  View Invoices
                </motion.button>
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => handleDeleteProperty(selectedProperty.id)}
                  className="px-4 py-2 border border-red-300 text-red-600 rounded-lg hover:bg-red-50 flex items-center gap-2 transition-colors"
                >
                  <Trash2 className="w-4 h-4" />
                  Delete
                </motion.button>
              </div>
            </div>
          </motion.div>
        </div>
      )}

      {/* Property Form Modal */}
      {(showCreateModal || editingProperty) && (
        <PropertyForm
          property={editingProperty}
          onSave={handleSaveProperty}
          onClose={() => {
            setShowCreateModal(false);
            setEditingProperty(null);
          }}
        />
      )}
    </div>
  );
};

export default Properties;
